import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Grid, CircularProgress, TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import { alertActions } from "_store";
import { getMatrixComplianceApi, saveMatrixComplianceApi } from "services/matrixCompliance/matrixCompliance.thunks";
import { ERR_MSG } from "constants/labels";
import dayjs from "dayjs";
import MatrixComplianceFilter from "./MatrixComplianceFilter";
import MatrixComplianceTable from "./MatrixComplianceTable";
import MatrixComplianceUpload from "./MatrixComplianceUpload";
import { buildDateRange, sanitizeNumber } from "./MatrixComplianceUtils";
import { useCheckRole, useActiveMarketerId } from "_utils/role-access";

const header = "Matrix Compliance";

const SectionCard = ({ children, sx = {} }) => (
  <Box sx={{ border: "1px solid #d6e4f0", borderRadius: "10px", p: 2, backgroundColor: "#FFFFFF", ...sx }}>
    {children}
  </Box>
);

const MatrixCompliance = () => {
  const dispatch = useDispatch();

  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userdetails = user?.UserDetails;
  const userId = userdetails?.id;
  const userAccess = user?.UserAccess;
  const isAdmin = userAccess?.some((access) => access.Role.toLowerCase().includes("admin"));
  const { activeMarketerId, marketers } = useActiveMarketerId();
  const canEdit = useCheckRole(4, "EditMatrixCompliance");
  const canEditSendoutThreshold = isAdmin;

  const [fromDate, setFromDate] = useState(
    isAdmin ? dayjs().subtract(6, "day").toDate() : dayjs().startOf("month").toDate()
  );
  const [toDate, setToDate] = useState(isAdmin ? dayjs().toDate() : dayjs().endOf("month").toDate());
  const [openComponent, setOpenComponent] = useState(null);

  const [threshold, setThreshold] = useState("");
  const [adminRows, setAdminRows] = useState([]);
  const [marketerRows, setMarketerRows] = useState([]);
  const [dateSendoutMap, setDateSendoutMap] = useState({});
  const [companyID, setCompanyID] = useState(0);
  const [companyName, setCompanyName] = useState("");

  const [isDataChanged, setIsDataChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const dateRange = useMemo(() => buildDateRange(fromDate, toDate), [fromDate, toDate]);

  const handleCloseBackdrop = () => setOpenComponent(null);
  const handleOpenComponent = (component) => setOpenComponent((prev) => (prev === component ? null : component));

  // ─── Resolve the current marketer's CompanyID from the active Switch Marketer selection ───
  const resolveCompanyID = () => {
    if (isAdmin) return 0;
    const marketer = marketers?.find((m) => m.MarketerId === activeMarketerId);
    setCompanyName(marketer?.MarketerName || "");
    return activeMarketerId || 0;
  };

  const buildAdminRows = (plannedInjectionsData, dates) => {
    const companyNames = [...new Set((plannedInjectionsData ?? []).map((d) => d.CompanyName))];
    return companyNames.map((name) => {
      const row = { CompanyName: name };
      dates.forEach((date) => {
        const match = plannedInjectionsData.find(
          (d) => d.CompanyName === name && dayjs(d.DateInjection).format("MM/DD/YYYY") === date
        );
        row[date] = match ? match.PlannedInjection : 0;
      });
      return row;
    });
  };

  const buildMarketerRows = (plannedInjectionsData, dates) =>
    dates.map((date) => {
      const match = (plannedInjectionsData ?? []).find(
        (d) => dayjs(d.DateInjection).format("MM/DD/YYYY") === date
      );
      return { date, sendout: match ? match.Sendout : 0, value: match ? match.PlannedInjection : 0 };
    });

  const buildDateSendoutMap = (plannedInjectionsData, dates) => {
    const map = {};
    dates.forEach((date) => {
      const match = (plannedInjectionsData ?? []).find(
        (d) => dayjs(d.DateInjection).format("MM/DD/YYYY") === date
      );
      map[date] = match ? match.Sendout : 0;
    });
    return map;
  };

  const fetchMatrixCompliance = async (id, dates, from, to) => {
    setIsLoading(true);
    try {
      const payload = {
        Data: {
          CompanyID: isAdmin ? 0 : id,
          StartDate: dayjs(from).format("YYYY-MM-DD"),
          EndDate: dayjs(to).format("YYYY-MM-DD"),
        },
      };
      const result = await dispatch(getMatrixComplianceApi(payload)).unwrap();
      const { MatrixComplianceData, PlannedInjectionsData } = result?.Data ?? {};

      setThreshold(String(MatrixComplianceData?.Threshold ?? ""));
      setDateSendoutMap(buildDateSendoutMap(PlannedInjectionsData, dates));

      if (isAdmin) {
        setAdminRows(buildAdminRows(PlannedInjectionsData, dates));
      } else {
        setMarketerRows(buildMarketerRows(PlannedInjectionsData, dates));
      }
    } catch (error) {
      dispatch(alertActions.clear());
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    } finally {
      setIsLoading(false);
    }
  };

  const initialize = async () => {
    const id = resolveCompanyID();
    setCompanyID(id);
    await fetchMatrixCompliance(id, dateRange, fromDate, toDate);
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarketerId]);

  const handleFilter = async (data) => {
    dispatch(alertActions.clear());
    const [start, end] = data.SelectedDate;
    const newFromDate = start.toDate();
    const newToDate = end.toDate();
    setFromDate(newFromDate);
    setToDate(newToDate);
    handleCloseBackdrop();

    const newDateRange = buildDateRange(newFromDate, newToDate);
    await fetchMatrixCompliance(companyID, newDateRange, newFromDate, newToDate);
  };

  const handleThresholdChange = (e) => {
    setThreshold(sanitizeNumber(e.target.value));
    setIsDataChanged(true);
  };

  const handleAdminCellChange = useCallback((companyNameKey, date, value) => {
    setAdminRows((prev) =>
      prev.map((row) => (row.CompanyName === companyNameKey ? { ...row, [date]: value } : row))
    );
    setIsDataChanged(true);
  }, []);

  const handleMarketerCellChange = useCallback((date, _colKey, value) => {
    setMarketerRows((prev) => prev.map((row) => (row.date === date ? { ...row, value } : row)));
    setIsDataChanged(true);
  }, []);

  const handleApplyAdminRows = (uploadMap) => {
    setAdminRows((prev) =>
      prev.map((row) => {
        const key = String(row.CompanyName).trim().toLowerCase();
        if (!(key in uploadMap)) return row;
        return { ...row, ...uploadMap[key] };
      })
    );
    setIsDataChanged(true);
  };

  const handleApplyMarketerRows = (uploadMap) => {
    setMarketerRows((prev) =>
      prev.map((row) => (row.date in uploadMap ? { ...row, value: uploadMap[row.date] } : row))
    );
    setIsDataChanged(true);
  };

  const handleCancel = () => {
    setIsDataChanged(false);
    fetchMatrixCompliance(companyID, dateRange, fromDate, toDate);
  };

  const handleSave = async () => {
    dispatch(alertActions.clear());

    try {
      let plannedInjectionsData;
      if (isAdmin) {
        plannedInjectionsData = adminRows.flatMap((row) =>
          dateRange.map((date) => ({
            CompanyName: row.CompanyName,
            DateInjection: dayjs(date, "MM/DD/YYYY").format("YYYY-MM-DDTHH:mm:ss"),
            PlannedInjection: Number(row[date] || 0),
          }))
        );
      } else {
        plannedInjectionsData = marketerRows.map((row) => ({
          CompanyName: companyName,
          DateInjection: dayjs(row.date, "MM/DD/YYYY").format("YYYY-MM-DDTHH:mm:ss"),
          PlannedInjection: Number(row.value || 0),
        }));
      }

      const payload = {
        Data: {
          MatrixComplianceData: {
            Threshold: Number(threshold) || 0,
          },
          PlannedInjections: plannedInjectionsData,
        },
      };

      const result = await dispatch(saveMatrixComplianceApi(payload)).unwrap();

      if (!result?.Succeeded) {
        dispatch(alertActions.error({ message: result?.Message || ERR_MSG["073"], header }));
        return;
      }

      dispatch(alertActions.success({ message: result?.Message || "Matrix Compliance saved successfully.", header }));
      setIsDataChanged(false);
      fetchMatrixCompliance(companyID, dateRange, fromDate, toDate);
    } catch (error) {
      dispatch(alertActions.error({ message: error.message ? ERR_MSG["073"] : error, header }));
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", py: 3, opacity: isLoading ? 0.5 : 1, transition: "opacity 0.2s" }}>
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.55)",
            backdropFilter: "blur(2px)",
          }}
        >
          <CircularProgress size={48} sx={{ color: "#1a3a5c" }} />
        </Box>
      )}

      <Grid2 container direction="row" spacing={{ xs: 2, md: 3 }} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid2 item xs={6} sm={6} md={6}>
          <Typography sx={{ color: "#0059B3", fontSize: "16px", fontWeight: 700 }}>Matrix Compliance</Typography>
        </Grid2>
        <Grid2 item xs={6} sm={6} md={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <MatrixComplianceFilter
            fromDate={fromDate}
            toDate={toDate}
            isOpen={openComponent === "filter"}
            onClose={handleCloseBackdrop}
            onOpen={() => handleOpenComponent("filter")}
            handleFilter={handleFilter}
          />
        </Grid2>
      </Grid2>

      <SectionCard sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>Threshold</Typography>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={threshold}
              onChange={handleThresholdChange}
              placeholder="0"
              disabled={!canEditSendoutThreshold}
              inputProps={{ inputMode: "numeric" }}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <SectionCard>
            {isAdmin && (
              <Typography sx={{ fontWeight: 700, color: "#1a3a5c", mb: 1 }}>Planned Injections</Typography>
            )}
            <MatrixComplianceTable
              isAdmin={isAdmin}
              rows={isAdmin ? adminRows : marketerRows}
              dateRange={dateRange}
              dateSendoutMap={dateSendoutMap}
              canEdit={canEdit}
              onCellChange={isAdmin ? handleAdminCellChange : handleMarketerCellChange}
              marketerName={companyName}
            />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <MatrixComplianceUpload
            isAdmin={isAdmin}
            rows={isAdmin ? adminRows : marketerRows}
            dateRange={dateRange}
            dateSendoutMap={dateSendoutMap}
            onApplyAdminRows={handleApplyAdminRows}
            onApplyMarketerRows={handleApplyMarketerRows}
            setIsDataChanged={setIsDataChanged}
            canEdit={canEdit}
          />
        </Grid>
      </Grid>

      {canEdit && (
        <Grid item xs={12} className="Personal-Information">
          <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className="submitbutton"
            onClick={handleSave}
            disabled={!isDataChanged}
          >
            Save
          </Button>
        </Grid>
      )}
    </Box>
  );
};

export default MatrixCompliance;
